import { getAuthHeaders, getBaseUrl } from './apiClient';
import { CourseStageRecord } from './courseRunsService';

export type DesignStatus =
  | 'created'
  | 'designing'
  | 'design_review'
  | 'approved'
  | 'design_failed';

export type RealizationTarget = 'simon' | 'talaera';

export interface RealizationState {
  status?: 'realizing' | 'package_ready' | 'building' | 'rendered' | 'failed';
  design_version?: number;
  course_run_id?: string;
  lesson_runs?: Array<{ lesson_run_id: string; lesson_number: number; build_job_id: string }>;
  checklist?: Record<string, boolean>;
}

export interface DesignRun {
  run_id: string;
  course_id: string;
  pipeline: 'course_design';
  design_status: DesignStatus;
  design_version: number;
  approved_version?: number;
  realizations: Partial<Record<RealizationTarget, RealizationState>>;
  stages: CourseStageRecord[];
  last_error?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface DesignAnswerOption {
  text: string;
  correct: boolean;
  why?: string;
}

export interface DesignIntent {
  intent_id: string;
  intent_type: string;
  assessment_goal: string;
  concept_refs?: string[];
  stimulus?: string;
  explanation_notes?: string;
  guidance?: string;
  payload: {
    options?: DesignAnswerOption[];
    pairs?: Array<{ left: string; right: string }>;
    frame?: string;
    answer?: string;
    distractors?: string[];
    question_structure?: { intent_type: string; options: DesignAnswerOption[] };
    task?: string;
    model_answer?: string;
    model_utterance?: string;
    rubric_points?: string[];
    question?: string;
  };
}

export interface DesignConcept {
  concept_id: string;
  kind: string;
  gist: string;
  teaching_points?: string[];
  stimulus?: string;
  content?: {
    items?: Array<{ text: string; meaning?: string; group?: string }>;
    example_exchanges?: Array<{ prompt: string; response: string; note?: string }>;
  };
}

export interface DesignFlowEntry {
  concept?: DesignConcept;
  intent?: DesignIntent;
}

export interface DesignAsset {
  asset_id: string;
  kind: string;
  brief: string;
  setting?: string;
  audio_type?: string;
  transcript?: string;
  image_brief?: string;
  speakers?: Array<{ label: string; gender?: string; voice_hint?: string }>;
  turns?: Array<{ speaker: string; text: string }>;
}

export interface SyllabusLesson {
  lesson_number: number;
  lesson_type: string;
  title: string;
  subtitle?: string;
  objectives: string[];
  skill_tags?: Array<Record<string, string>>;
  prerequisites?: string;
  exit_knowledge?: string;
  time_budget?: { reading_minutes?: number; exercises_minutes?: number; exercise_count_target?: number };
  targeting_context?: string;
}

export interface CourseDesign {
  design_version: number;
  scope: {
    course_title: string;
    course_description: string;
    icp?: string;
    learning_objectives: string[];
    test_policy: string;
    /** Words the CEFR vocabulary gate must allow for this course (user override). */
    vocab_exceptions?: string[];
    directional_guidance?: string;
    lesson_count_hint?: number | string;
    lesson_count_mode?: 'approx' | 'exact';
    duration_minutes_hint?: number | string;
    targeting: {
      cefr_level: string;
      audience: string;
      industry_focus?: string;
      english_variant?: string;
      skill_focus?: Array<Record<string, unknown>>;
    };
  };
  syllabus: {
    narrative_arc?: string;
    structured_goals?: string[];
    course_glossary?: Array<{ term: string; definition: string }>;
    lessons: SyllabusLesson[];
  } | null;
  lessons: Array<{ lesson_number: number; flow: DesignFlowEntry[] }>;
  assets: DesignAsset[];
  closing?: { use_standard_cta: boolean; custom_note?: string };
}

export interface SimonPackage {
  course_json: string;
  build_spec_md: string;
  asset_manifest: Array<{ filename: string; used_in: string[] }>;
  assertions: { section_positions: number[]; counts: Record<string, number> };
}

export interface SimonCourseSetup {
  title: string;
  description?: string;
  subtitle?: string;
  author?: string;
  learning_objectives?: string[];
  style?: { publisher_logo?: string; cover_background?: string; theme?: string; background_music?: string };
  settings?: { advance_after_voiceover?: boolean; sequential_order?: boolean };
}

export interface SimonBlock {
  type: string;
  title?: string;
  body?: string;
  image?: string;
  video?: string;
  voiceover?: { mode: string; file: string };
  narration?: string;
  role?: string;
  stimulus_ref?: string;
  questions?: Array<{
    text: string;
    select: string;
    answers: Array<{ text: string; correct: boolean }>;
    explanation?: string;
  }>;
}

export interface DesignDocument {
  design: CourseDesign;
  realizations?: {
    simon?: {
      course_doc?: {
        course?: SimonCourseSetup;
        blocks: SimonBlock[];
        assets?: string[];
        substitutions?: Array<{ intent_id: string; reason: string }>;
        level_flags?: Array<{ section: number; index: number; reason: string }>;
        drafting_flags?: Array<{ section: number; index: number; reason: string }>;
      };
      validation?: {
        validated: boolean;
        issues?: Array<{ rule?: number; message: string; block_index?: number }>;
      };
      assets?: { zip_key: string; files: Array<{ filename: string; status: string; qa_warning?: string }>; pending_videos: string[]; rendering_videos?: string[]; failed_videos?: string[] };
      package?: SimonPackage;
    };
    talaera?: Record<string, unknown>;
  };
}

const base = () => `${getBaseUrl()}/secure/courses/design-runs`;

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(url, { ...init, headers: await getAuthHeaders() });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok && resp.status !== 422) {
    throw new Error((body as { error?: string }).error ?? `${resp.status}`);
  }
  return body as T;
}

export const designRunsService = {
  async list(courseId?: string): Promise<{ runs: DesignRun[] }> {
    const qs = courseId ? `?course_id=${encodeURIComponent(courseId)}` : '';
    return request(`${base()}${qs}`);
  },
  async get(runId: string): Promise<DesignRun> {
    return request(`${base()}/${runId}`);
  },
  async create(courseId: string): Promise<{ run_id: string; job_id: string }> {
    return request(base(), { method: 'POST', body: JSON.stringify({ course_id: courseId }) });
  },
  async getDocument(runId: string): Promise<DesignDocument> {
    return request(`${base()}/${runId}/document`);
  },
  async putDesign(runId: string, design: CourseDesign): Promise<{ design_version?: number; issues: string[] }> {
    return request(`${base()}/${runId}/design`, {
      method: 'PUT',
      body: JSON.stringify({ design }),
    });
  },
  async putBrief(runId: string, brief: Partial<CourseDesign['scope']>): Promise<{ design_version?: number; issues: string[] }> {
    return request(`${base()}/${runId}/brief`, {
      method: 'PUT',
      body: JSON.stringify({ brief }),
    });
  },
  async approve(runId: string): Promise<{ design_version?: number; issues?: string[] }> {
    return request(`${base()}/${runId}/approve`, { method: 'POST', body: '{}' });
  },
  async rerunDesign(runId: string): Promise<{ job_id: string }> {
    return request(`${base()}/${runId}/design-phase`, { method: 'POST', body: '{}' });
  },
  async realize(runId: string, target: RealizationTarget): Promise<{ job_id: string }> {
    return request(`${base()}/${runId}/realize`, {
      method: 'POST',
      body: JSON.stringify({ target }),
    });
  },
  async getPackage(runId: string): Promise<SimonPackage> {
    return request(`${base()}/${runId}/package`);
  },
  async setChecklist(runId: string, target: RealizationTarget, item: string, done: boolean): Promise<void> {
    await request(`${base()}/${runId}/checklist`, {
      method: 'PUT',
      body: JSON.stringify({ target, item, done }),
    });
  },
};
